export function first_value(values, fallback = '') {
  const value = values.find(function find_present_value(candidate) {
    return candidate !== undefined && candidate !== null && candidate !== '';
  });

  return value === undefined ? fallback : value;
}

export function normalize_text(value) {
  return String(value || '').trim().toLowerCase();
}

function title_word(word) {
  return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`;
}

export function title_case(value) {
  const text = String(value || '').trim();

  if (!text) {
    return '';
  }

  return text.split(/\s+/).map(title_word).join(' ');
}

const STATUS_TONE_RULES = [
  { tone: 'success', words: ['success', 'passed', 'pass', 'verified', 'clean', 'ok', 'complete'] },
  { tone: 'danger', words: ['fail', 'failed', 'failure', 'mismatch', 'rejected', 'error', 'expired', 'blocked'] },
  { tone: 'warning', words: ['pending', 'manual', 'review', 'timeout', 'awaiting', 'queued'] }
];

function status_includes_word(normalized, word) {
  return normalized.includes(word);
}

function status_matches_rule(normalized, rule) {
  return rule.words.some(function includes_word(word) {
    return status_includes_word(normalized, word);
  });
}

export function get_verification_status_tone(status) {
  const normalized = normalize_text(status);
  const rule = STATUS_TONE_RULES.find(function find_rule(candidate) {
    return normalized && status_matches_rule(normalized, candidate);
  });

  return rule?.tone || 'secondary';
}

export function get_verification_impact(tone) {
  if (tone === 'danger') {
    return 'Blocks progress';
  }

  if (tone === 'warning') {
    return 'Needs review';
  }

  if (tone === 'success') {
    return 'Can proceed';
  }

  return 'Recorded';
}

export function format_log_timestamp(timestamp) {
  if (!timestamp) {
    return '-';
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return String(timestamp);
  }

  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

export function get_statuses_for_form(statuses_by_form, application_form_id) {
  if (!statuses_by_form || typeof statuses_by_form !== 'object') {
    return {};
  }

  return Reflect.get(statuses_by_form, application_form_id) || {};
}

function build_service_name_entry(service) {
  const normalized_name = normalize_text(first_value([
    service?.name,
    service?.service_name,
    service?.title
  ], ''));

  return normalized_name ? [normalized_name, service] : null;
}

export function build_service_name_map(services = []) {
  const entries = (Array.isArray(services) ? services : [])
    .map(build_service_name_entry)
    .filter(Boolean);
  const service_name_map = new Map(entries);

  return service_name_map;
}

export function resolve_service_from_candidates({
  service_map,
  service_name_map,
  service_id,
  service_name
}) {
  const direct_service = service_map.get(String(first_value([service_id], '')));
  if (direct_service) {
    return direct_service;
  }

  const normalized_name = normalize_text(service_name);
  if (!normalized_name) {
    return {};
  }

  return service_name_map.get(normalized_name) || {};
}

export function get_case_identifier(case_item = {}) {
  return first_value([case_item?.id, case_item?.application_id], 'case');
}

export function get_case_name(case_item = {}) {
  return case_item?.name || 'Onboarded Entity';
}

export function normalize_verification_type(value = '') {
  return normalize_text(value).replace(/[_-]+/g, ' ');
}

function get_service_values(service = {}) {
  return [
    service?.verification_type,
    service?.name,
    service?.service_name,
    service?.title
  ].map(normalize_verification_type).filter(Boolean);
}

function service_value_matches_type(value, normalized_type) {
  return value === normalized_type
    || value.includes(normalized_type)
    || normalized_type.includes(value);
}

export function service_matches_verification_type(service = {}, verification_type = '') {
  const normalized_type = normalize_verification_type(verification_type);
  const service_values = get_service_values(service);

  return service_values.some(function matches_type(value) {
    return service_value_matches_type(value, normalized_type);
  });
}

export function sort_checks_by_last_run(checks) {
  return checks.sort(function sort_check(left, right) {
    return String(right.last_run).localeCompare(String(left.last_run));
  });
}
